<template>
  <div
    class="appointment-report ar-select"
    :class="{
      'ar-select--open': open,
      'ar-select--invalid': showError,
      'ar-select--disabled': disabled,
    }"
    :style="{ maxWidth }"
    @focusout="onFocusOut"
  >
    <label
      :id="labelId"
      :for="triggerId"
      class="ar-select__label"
    >{{ resolvedLabel }}</label>

    <div
      ref="anchor"
      class="ar-select__anchor"
    >
      <button
        :id="triggerId"
        ref="trigger"
        type="button"
        class="ar-select__trigger"
        aria-haspopup="listbox"
        :aria-expanded="open ? 'true' : 'false'"
        :aria-controls="open ? listId : null"
        :aria-labelledby="`${labelId} ${valueId}`"
        :aria-describedby="showError ? errorId : null"
        :aria-invalid="showError ? 'true' : null"
        :disabled="isInert"
        @click="toggle"
        @keydown="onTriggerKeydown"
      >
        <span
          :id="valueId"
          class="ar-select__value"
          :class="{ 'ar-select__value--placeholder': !selectedOption }"
        >{{ selectedOption ? selectedOption.label : resolvedPlaceholder }}</span>
        <feather-icon
          icon="ChevronDownIcon"
          size="16"
          class="ar-select__chevron"
          aria-hidden="true"
        />
      </button>

      <transition name="ar-pop">
        <ul
          v-if="open"
          :id="listId"
          ref="list"
          role="listbox"
          :aria-labelledby="labelId"
          class="ar-select__list"
          :class="{ 'ar-select__list--up': dropUp }"
          @mousedown.prevent
        >
          <li
            v-for="(option, index) in resolvedOptions"
            :key="option.value"
            role="none"
          >
            <button
              :id="optionId(index)"
              ref="options"
              type="button"
              role="option"
              tabindex="-1"
              class="ar-select__option"
              :class="{ 'ar-select__option--selected': option.value === value }"
              :aria-selected="option.value === value ? 'true' : 'false'"
              @click="choose(option)"
              @keydown="onOptionKeydown($event, index)"
            >
              <span class="ar-select__option-label">{{ option.label }}</span>
              <feather-icon
                v-if="option.value === value"
                icon="CheckIcon"
                size="16"
                class="ar-select__check"
                aria-hidden="true"
              />
            </button>
          </li>
        </ul>
      </transition>
    </div>

    <span
      v-if="showError"
      :id="errorId"
      class="ar-select__error ar-anim-fade-in"
    >{{ resolvedErrorMessage }}</span>
  </div>
</template>

<script>
import { NEW_COPY, REASON_PLACEHOLDER, REASON_SELECTS } from '@/constants/appointment_report_v2'
import { nextReportUid } from '../helpers/report_ui'

const TYPEAHEAD_RESET_MS = 500

const normalize = text => String(text || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()

const isPrintableKey = event => !!event.key
  && event.key.length === 1
  && event.key !== ' '
  && !event.ctrlKey
  && !event.metaKey
  && !event.altKey

/**
 * Select de motivo (listbox — spec-v4): gatilho de 44px com chevron que gira, popover e2 com check na opção
 * marcada, abre com fade + scale .98→1 (160ms). Teclado: Enter/Espaço/setas abrem; setas, Home/End e digitação
 * (typeahead, ignora acentos) navegam; Enter/Espaço escolhem; Esc fecha e volta ao gatilho; Tab fecha.
 *
 *   <report-reason-select v-model="answers.not_held_reason" reason-key="not_held_reason" :invalid="…" />
 *
 * `v-model` usa as CHAVES do backend (`AppointmentReportReasons.php`). `reason-key` puxa rótulo e opções de
 * `REASON_SELECTS`; `label`/`options` sobrescrevem (ex.: motivos legados de Entrevista/Negociação).
 * `focus()` leva o foco ao gatilho.
 */
export default {
  name: 'ReportReasonSelect',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: String,
      default: null,
    },
    // `not_held_reason` | `offer_not_made_reason` (constantes de `appointment_report_v2`).
    reasonKey: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // Bloqueia sem esmaecer (ex.: enquanto finaliza).
    readonly: {
      type: Boolean,
      default: false,
    },
    idPrefix: {
      type: String,
      default: '',
    },
    maxWidth: {
      type: String,
      default: '520px',
    },
    // Espaço (px) coberto por uma barra fixa no rodapé: se a lista não couber acima dele, abre para cima.
    bottomInset: {
      type: Number,
      default: 88,
    },
  },
  data() {
    return {
      fallbackUid: nextReportUid('ar-sel'),
      open: false,
      dropUp: false,
      typeBuffer: '',
    }
  },
  computed: {
    uid() {
      return this.idPrefix || this.fallbackUid
    },
    config() {
      return REASON_SELECTS[this.reasonKey] || null
    },
    resolvedOptions() {
      if (Array.isArray(this.options)) return this.options
      return this.config ? this.config.options : []
    },
    resolvedLabel() {
      return this.label || (this.config ? this.config.label : '')
    },
    resolvedPlaceholder() {
      return this.placeholder || REASON_PLACEHOLDER
    },
    resolvedErrorMessage() {
      return this.errorMessage || NEW_COPY.answerRequired
    },
    selectedIndex() {
      return this.resolvedOptions.findIndex(option => option.value === this.value)
    },
    selectedOption() {
      return this.selectedIndex >= 0 ? this.resolvedOptions[this.selectedIndex] : null
    },
    isInert() {
      return this.disabled || this.readonly
    },
    // A mensagem continua visível com a lista aberta (board v4-report); a borda de erro vence a de "aberto".
    showError() {
      return this.invalid
    },
    labelId() {
      return `${this.uid}-label`
    },
    triggerId() {
      return `${this.uid}-trigger`
    },
    valueId() {
      return `${this.uid}-value`
    },
    listId() {
      return `${this.uid}-list`
    },
    errorId() {
      return `${this.uid}-error`
    },
  },
  watch: {
    isInert(inert) {
      if (inert && this.open) this.close()
    },
  },
  beforeDestroy() {
    this.stopOutsideListener()
    clearTimeout(this.typeTimer)
  },
  methods: {
    optionId(index) {
      return `${this.uid}-option-${index}`
    },
    focus(options) {
      if (this.$refs.trigger) this.$refs.trigger.focus(options)
    },
    openAt(index) {
      if (this.isInert || !this.resolvedOptions.length) return
      const wasOpen = this.open
      this.open = true
      if (!wasOpen) {
        this.dropUp = false
        this.startOutsideListener()
        this.$emit('open')
      }
      this.$nextTick(() => {
        if (!wasOpen) this.placeList()
        this.focusOption(index)
      })
    },
    close(options) {
      if (!this.open) return
      this.open = false
      this.stopOutsideListener()
      this.$emit('close')
      if (options && options.focusTrigger) this.$nextTick(() => this.focus())
    },
    toggle() {
      if (this.open) {
        this.close({ focusTrigger: true })
        return
      }
      this.openAt(this.selectedIndex >= 0 ? this.selectedIndex : 0)
    },
    choose(option) {
      if (this.isInert) return
      if (option.value !== this.value) {
        this.$emit('input', option.value)
        this.$emit('change', option.value)
      }
      this.close({ focusTrigger: true })
    },
    focusOption(index) {
      const options = this.$refs.options || []
      const count = options.length
      if (!count) return
      const target = options[((index % count) + count) % count]
      if (target) target.focus()
    },
    // Abre para cima quando a lista não cabe entre o gatilho e a barra fixa do rodapé (e cabe acima).
    placeList() {
      const { anchor, list } = this.$refs
      if (!anchor || !list || typeof window === 'undefined') return
      const rect = anchor.getBoundingClientRect()
      const listHeight = list.offsetHeight + 8
      const spaceBelow = window.innerHeight - this.bottomInset - rect.bottom
      this.dropUp = spaceBelow < listHeight && rect.top > spaceBelow && rect.top >= listHeight
    },
    typeahead(key, fromIndex) {
      clearTimeout(this.typeTimer)
      this.typeBuffer += normalize(key)
      this.typeTimer = setTimeout(() => {
        this.typeBuffer = ''
      }, TYPEAHEAD_RESET_MS)

      const buffer = this.typeBuffer
      const repeated = buffer.split('').every(char => char === buffer.charAt(0))
      const search = repeated ? buffer.charAt(0) : buffer
      // Mesma letra repetida → percorre as opções que começam com ela; texto → procura a partir da atual.
      const start = repeated ? fromIndex + 1 : Math.max(fromIndex, 0)
      const count = this.resolvedOptions.length
      for (let step = 0; step < count; step += 1) {
        const index = (((start + step) % count) + count) % count
        if (normalize(this.resolvedOptions[index].label).startsWith(search)) return index
      }

      return -1
    },
    onTriggerKeydown(event) {
      if (this.isInert) return
      const count = this.resolvedOptions.length
      const current = this.selectedIndex
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          this.openAt(current >= 0 ? current : 0)
          return
        case 'ArrowUp':
          event.preventDefault()
          this.openAt(current >= 0 ? current : count - 1)
          return
        case 'Enter':
        case ' ':
          event.preventDefault()
          if (this.open) this.close({ focusTrigger: true })
          else this.openAt(current >= 0 ? current : 0)
          return
        case 'Escape':
          if (this.open) {
            event.preventDefault()
            this.close({ focusTrigger: true })
          }
          return
        default:
          if (isPrintableKey(event)) {
            const index = this.typeahead(event.key, current)
            if (index >= 0) {
              event.preventDefault()
              this.openAt(index)
            }
          }
      }
    },
    onOptionKeydown(event, index) {
      const count = this.resolvedOptions.length
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          this.focusOption(index + 1)
          return
        case 'ArrowUp':
          event.preventDefault()
          this.focusOption(index - 1)
          return
        case 'Home':
          event.preventDefault()
          this.focusOption(0)
          return
        case 'End':
          event.preventDefault()
          this.focusOption(count - 1)
          return
        case 'Escape':
          event.preventDefault()
          this.close({ focusTrigger: true })
          return
        case 'Tab':
          this.close()
          return
        default:
          if (isPrintableKey(event)) {
            const match = this.typeahead(event.key, index)
            if (match >= 0) {
              event.preventDefault()
              this.focusOption(match)
            }
          }
      }
    },
    onFocusOut(event) {
      if (!this.open) return
      const next = event.relatedTarget
      if (next && this.$el.contains(next)) return
      this.close()
    },
    onOutsidePointer(event) {
      if (this.$el && !this.$el.contains(event.target)) this.close()
    },
    startOutsideListener() {
      document.addEventListener('mousedown', this.onOutsidePointer, true)
      document.addEventListener('touchstart', this.onOutsidePointer, true)
    },
    stopOutsideListener() {
      document.removeEventListener('mousedown', this.onOutsidePointer, true)
      document.removeEventListener('touchstart', this.onOutsidePointer, true)
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>

<style lang="scss" scoped>
.ar-select {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ar-select__label {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-select__anchor {
  position: relative;
}

// Acima da barra fixa do rodapé enquanto aberto.
.ar-select--open .ar-select__anchor {
  z-index: 30;
}

.ar-select__trigger {
  width: 100%;
  height: 44px;
  margin: 0;
  padding: 0 12px 0 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--ar-control);
  border-radius: var(--ar-radius-control);
  background: var(--ar-bg-field);
  color: var(--ar-text-1);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color var(--ar-dur-hover) var(--ar-ease-standard),
    background-color var(--ar-dur-hover) var(--ar-ease-standard);

  &:hover:not(:disabled) {
    border-color: var(--ar-control-hover);
    background: var(--ar-bg-field-hover);
  }

  &:disabled {
    cursor: default;
  }
}

.ar-select--open .ar-select__trigger,
.ar-select--open .ar-select__trigger:hover:not(:disabled) {
  border-color: var(--ar-brand-text);
}

.ar-select--invalid .ar-select__trigger,
.ar-select--invalid .ar-select__trigger:hover:not(:disabled) {
  border-color: var(--ar-warning);
}

.ar-select--disabled .ar-select__trigger {
  opacity: .56;
}

.ar-select__value {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-1);
}

.ar-select__value--placeholder {
  font-weight: 400;
  color: var(--ar-text-placeholder);
}

.ar-select__chevron {
  flex-shrink: 0;
  color: var(--ar-text-2);
  transition: transform var(--ar-dur-fast) var(--ar-ease-standard);
}

.ar-select--open .ar-select__chevron {
  transform: rotate(180deg);
}

// ---- Popover (e2) ----
.ar-select__list {
  position: absolute;
  top: 48px;
  right: 0;
  left: 0;
  z-index: 30;
  max-height: 320px;
  margin: 0;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  list-style: none;
  border: 1px solid var(--ar-divider);
  border-radius: var(--ar-radius-card);
  background: var(--ar-bg-raised);
  box-shadow: var(--ar-shadow-e2);
  transform-origin: top center;

  > li {
    margin: 0;
    padding: 0;
  }
}

.ar-select__list--up {
  top: auto;
  bottom: 48px;
  transform-origin: bottom center;
}

.ar-select__option {
  width: 100%;
  min-height: 40px;
  margin: 0;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 0;
  border-radius: var(--ar-radius-control);
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: var(--ar-text-2);
  text-align: left;
  cursor: pointer;
  transition:
    background-color var(--ar-dur-hover) var(--ar-ease-standard),
    color var(--ar-dur-hover) var(--ar-ease-standard);

  &:hover,
  &:focus-visible {
    background: var(--ar-bg-raised-hover);
    color: var(--ar-text-1);
  }

  &:focus-visible {
    outline-offset: -2px;
  }
}

.ar-select__option--selected {
  background: var(--ar-selected-tint);
  font-weight: 500;
  color: var(--ar-text-1);

  &:hover,
  &:focus-visible {
    background: var(--ar-selected-tint-hover);
  }
}

.ar-select__option-label {
  flex: 1 1 auto;
  min-width: 0;
}

.ar-select__check {
  flex-shrink: 0;
  color: var(--ar-brand-text);
}

.ar-select__error {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-warning);
}
</style>
